import { useState, useEffect } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { motion } from "framer-motion";
import { Flame, Sparkles, CalendarDays, Library, Bookmark, Radio, MessageCircle, Instagram, LogOut, Compass, Camera, MessageSquareText } from "lucide-react";
import Generator from "@/components/Generator";
import Strategia from "@/components/Strategia";
import Planner from "@/components/Planner";
import Archive from "@/components/Archive";
import SavedIdeas from "@/components/SavedIdeas";
import Attualita from "@/components/Attualita";
import DmScript from "@/components/DmScript";
import InstagramDashboard from "@/components/InstagramDashboard";
import StorieOggi from "@/components/StorieOggi";
import Commenti from "@/components/Commenti";
import StudioLogin from "@/components/StudioLogin";
import { getToken, checkAuth, clearToken } from "@/lib/api";

const TABS = [
  { id: "strategia", label: "Strategia", icon: Compass },
  { id: "attualita", label: "Attualità", icon: Radio },
  { id: "salvate", label: "Salvate", icon: Bookmark },
  { id: "generatore", label: "Generatore", icon: Sparkles },
  { id: "calendario", label: "Calendario", icon: CalendarDays },
  { id: "archivio", label: "Archivio", icon: Library },
  { id: "storie", label: "Storie", icon: Camera },
  { id: "commenti", label: "Commenti", icon: MessageSquareText },
  { id: "dm", label: "Script DM", icon: MessageCircle },
  { id: "instagram", label: "Andamento", icon: Instagram },
];

function App() {
  const [tab, setTab] = useState("strategia");
  const [prefill, setPrefill] = useState(null);
  const [savedKey, setSavedKey] = useState(0);
  const [authed, setAuthed] = useState(null); // null=checking, true, false

  useEffect(() => {
    document.title = "Studio — Massimiliano Pulla";
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!getToken()) {
      setAuthed(false);
    } else {
      checkAuth()
        .then(() => mounted && setAuthed(true))
        .catch(() => mounted && setAuthed(false));
    }
    const onUnauth = () => mounted && setAuthed(false);
    window.addEventListener("studio-unauthorized", onUnauth);
    return () => {
      mounted = false;
      window.removeEventListener("studio-unauthorized", onUnauth);
    };
  }, []);

  const logout = () => {
    clearToken();
    setAuthed(false);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tab");
    const ig = params.get("ig");
    if (t && TABS.some((x) => x.id === t)) setTab(t);
    if (ig === "connected") toast.success("Instagram collegato! Premi Sincronizza per scaricare i tuoi reel.");
    else if (ig === "error") toast.error(`Collegamento non riuscito: ${params.get("msg") || "riprova"}`);
    if (t || ig) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const handleUseIdea = (idea) => {
    setPrefill({ ...idea, _ts: Date.now() });
    setTab("generatore");
  };

  if (authed === null) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#D92D20] border-t-transparent rounded-full animate-spin" data-testid="studio-loading" />
      </div>
    );
  }

  if (!authed) {
    return <StudioLogin onSuccess={() => setAuthed(true)} />;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-neutral-100">
      <Toaster theme="dark" position="top-right" richColors />

      {/* Header */}
      <header className="border-b border-[#262626] sticky top-0 z-30 bg-[#0A0A0A]/90 backdrop-blur">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-[#D92D20] flex items-center justify-center">
              <Flame size={18} className="text-white" />
            </div>
            <div>
              <p className="font-display font-extrabold text-lg leading-none tracking-tight">STUDIO</p>
              <p className="text-[0.65rem] uppercase tracking-[0.25em] text-neutral-500 mt-0.5">Massimiliano Pulla</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-xs text-neutral-600 max-w-xs text-right leading-tight">
              Contenuti polarizzanti per coppie italiane sotto pressione
            </span>
            <button
              onClick={logout}
              data-testid="studio-logout"
              title="Esci"
              className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-white border border-[#262626] hover:border-[#404040] rounded-md px-3 py-1.5 transition-colors"
            >
              <LogOut size={14} />
              <span className="hidden sm:inline">Esci</span>
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-[1400px] mx-auto px-6 pt-8">
        <div className="flex gap-1 border-b border-[#262626] mb-8 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`relative flex items-center gap-2 px-4 py-3 text-sm font-semibold transition-colors whitespace-nowrap ${
                  active ? "text-white" : "text-neutral-500 hover:text-neutral-300"
                }`}
              >
                <Icon size={16} />
                {t.label}
                {active && (
                  <motion.div layoutId="tab-underline" className="absolute left-0 right-0 -bottom-px h-0.5 bg-[#D92D20]" />
                )}
              </button>
            );
          })}
        </div>

        <main className="pb-20">
          {tab === "strategia" && <Strategia />}
          {tab === "attualita" && <Attualita onUseIdea={handleUseIdea} onSavedChange={() => setSavedKey((k) => k + 1)} />}
          {tab === "salvate" && <SavedIdeas onUseIdea={handleUseIdea} reloadKey={savedKey} />}
          {tab === "generatore" && <Generator prefill={prefill} />}
          {tab === "calendario" && <Planner />}
          {tab === "archivio" && <Archive />}
          {tab === "storie" && <StorieOggi />}
          {tab === "commenti" && <Commenti />}
          {tab === "dm" && <DmScript />}
          {tab === "instagram" && <InstagramDashboard onUseIdea={handleUseIdea} />}
        </main>
      </div>
    </div>
  );
}

export default App;
